import Cookies from "js-cookie";
import {
  IRbacContext,
  IRbacAssignment,
  IRbacRoleOption,
  IRbacUserOption,
  IRbacOrgTypeOption,
} from "./Rbac.types";

const AUTH_API_BASE =
  process.env.REACT_APP_AUTH_BACKEND_SERVICE_URL || "";
const TOKEN_KEY = process.env.REACT_APP_AUTH_COOKIE_NAME || "access_token";
const AGL_DASHBOARD_APP_ID = 2;

function getAuthHeaders(): Record<string, string> {
  const accessToken =
    Cookies.get(TOKEN_KEY) ?? localStorage.getItem(TOKEN_KEY);
  if (!accessToken) return {};
  return {
    Accept: "application/json",
    "Content-Type": "application/json",
    Authorization: accessToken.trim(),
  };
}

export default class RbacApi {
  static async getUserContext(): Promise<IRbacContext | null> {
    if (!AUTH_API_BASE) return null;

    const headers = getAuthHeaders();
    if (!headers.Authorization) return null;

    try {
      const url = `${AUTH_API_BASE.replace(/\/$/, "")}/api/user-context?app_id=${AGL_DASHBOARD_APP_ID}`;
      const res = await fetch(url, {
        method: "GET",
        headers,
        credentials: "omit",
      });

      if (res.status === 401) {
        const { handleAuthFailure } = await import("../../settings");
        handleAuthFailure();
        return null;
      }

      if (!res.ok) return null;

      const body = await res.json();
      if (body?.status && body?.data) {
        return body.data as IRbacContext;
      }
      return null;
    } catch {
      return null;
    }
  }

  static async viewAsRole(roleId: number): Promise<IRbacContext | null> {
    if (!AUTH_API_BASE) return null;

    const headers = getAuthHeaders();
    if (!headers.Authorization) return null;

    try {
      const url = `${AUTH_API_BASE.replace(/\/$/, "")}/api/view-as-role`;
      const res = await fetch(url, {
        method: "POST",
        headers,
        credentials: "omit",
        body: JSON.stringify({
          role_id: roleId,
          app_id: AGL_DASHBOARD_APP_ID,
        }),
      });

      if (!res.ok) return null;

      const body = await res.json();
      if (body?.status && body?.data) {
        return body.data as IRbacContext;
      }
      return null;
    } catch {
      return null;
    }
  }

  static async getMyAssignments(): Promise<IRbacAssignment[]> {
    if (!AUTH_API_BASE) return [];

    const headers = getAuthHeaders();
    if (!headers.Authorization) return [];

    try {
      const url = `${AUTH_API_BASE.replace(/\/$/, "")}/api/my-assignments`;
      const res = await fetch(url, {
        method: "GET",
        headers,
        credentials: "omit",
      });

      if (!res.ok) return [];

      const body = await res.json();
      if (body?.status && body?.data) {
        return body.data as IRbacAssignment[];
      }
      return [];
    } catch {
      return [];
    }
  }

  static async viewAsUser(targetUserId: number): Promise<IRbacContext | null> {
    if (!AUTH_API_BASE) return null;

    const headers = getAuthHeaders();
    if (!headers.Authorization) return null;

    try {
      const url = `${AUTH_API_BASE.replace(/\/$/, "")}/api/view-as`;
      const res = await fetch(url, {
        method: "POST",
        headers,
        credentials: "omit",
        body: JSON.stringify({
          target_user_id: targetUserId,
          app_id: AGL_DASHBOARD_APP_ID,
        }),
      });

      if (!res.ok) return null;

      const body = await res.json();
      if (body?.status && body?.data) {
        return body.data as IRbacContext;
      }
      return null;
    } catch {
      return null;
    }
  }

  static async searchUsers(search: string): Promise<IRbacUserOption[]> {
    if (!AUTH_API_BASE) return [];

    const headers = getAuthHeaders();
    if (!headers.Authorization) return [];

    try {
      const url = `${AUTH_API_BASE.replace(/\/$/, "")}/api/user/listing/`;
      const res = await fetch(url, {
        method: "POST",
        headers,
        credentials: "omit",
        body: JSON.stringify({
          page: 1,
          page_size: 20,
          filters: search
            ? [{ column_name: "entity_url_email", type: "like", value: search }]
            : [],
        }),
      });

      if (!res.ok) return [];

      const body = await res.json();
      if (body?.status && body?.data) {
        return body.data.map((u: any) => ({
          id: u.id,
          first_name: u.first_name ?? u.firstName ?? "",
          last_name: u.last_name ?? u.lastName ?? "",
          email: u.entity_url_email ?? u.email ?? u.username ?? "",
        }));
      }
      return [];
    } catch {
      return [];
    }
  }

  static async getOrgTypes(): Promise<IRbacOrgTypeOption[]> {
    if (!AUTH_API_BASE) return [];

    const headers = getAuthHeaders();
    if (!headers.Authorization) return [];

    try {
      const url = `${AUTH_API_BASE.replace(/\/$/, "")}/api/org-types`;
      const res = await fetch(url, {
        method: "GET",
        headers,
        credentials: "omit",
      });

      if (!res.ok) return [];

      const body = await res.json();
      const data = body?.data ?? body;
      if (!Array.isArray(data)) return [];
      return data.map((ot: any) => ({
        id: ot.id,
        name: ot.name,
        display_name: ot.display_name ?? ot.displayName ?? ot.name,
      }));
    } catch {
      return [];
    }
  }

  static async getUsersByOrgType(orgTypeId: number): Promise<IRbacUserOption[]> {
    if (!AUTH_API_BASE) return [];

    const headers = getAuthHeaders();
    if (!headers.Authorization) return [];

    try {
      const url = `${AUTH_API_BASE.replace(/\/$/, "")}/api/organizations?org_type_id=${orgTypeId}&include_users=true`;
      const res = await fetch(url, {
        method: "GET",
        headers,
        credentials: "omit",
      });

      if (!res.ok) return [];

      const body = await res.json();
      const orgs = body?.data ?? body;
      if (!Array.isArray(orgs)) return [];

      const users: IRbacUserOption[] = [];
      for (const org of orgs) {
        const userOrgs = org.user_organizations ?? org.userOrganizations ?? [];
        for (const uo of userOrgs) {
          const u = uo.user;
          if (!u) continue;
          users.push({
            id: u.id,
            first_name: u.first_name ?? u.firstName ?? "",
            last_name: u.last_name ?? u.lastName ?? "",
            email: u.entity_url_Email ?? u.email ?? u.username ?? "",
            organization: org.name ?? "",
            role: uo.role?.display_name ?? uo.role?.name ?? "",
          });
        }
      }
      return users;
    } catch {
      return [];
    }
  }

  static async getRolesByOrgType(orgTypeId: number): Promise<IRbacRoleOption[]> {
    if (!AUTH_API_BASE) return [];

    const headers = getAuthHeaders();
    if (!headers.Authorization) return [];

    try {
      const url = `${AUTH_API_BASE.replace(/\/$/, "")}/api/roles?org_type_id=${orgTypeId}`;
      const res = await fetch(url, {
        method: "GET",
        headers,
        credentials: "omit",
      });

      if (!res.ok) return [];

      const body = await res.json();
      if (body?.status && body?.data) {
        return body.data.map((r: any) => ({
          id: r.id,
          name: r.name,
          displayName: r.display_name ?? r.displayName ?? r.name,
          organization: {
            id: r.organization?.id,
            name: r.organization?.name,
          },
        }));
      }
      return [];
    } catch {
      return [];
    }
  }
}
